import { useCallback, useEffect, useRef, useState } from "react";
import { AiOutlineArrowDown } from "react-icons/ai";
import { ImSpinner2 } from "react-icons/im";
import classNames from "classnames";

const Select = (props) => {
  // props
  const {
    placeholder,
    options,
    optionLabelProp,
    isDisabled,
    onSelect,
    initialValue,
    isLoading,
    isMulti,
    allowClear = true,
    direction: contentDirection,
    optionsDirection,
  } = props;
  // state
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(isMulti ? [] : "");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  // refs
  const selectRef = useRef();

  const removeSelectedValueHandler = (optionIdToRemove) => {
    const nextSelectedValue = Array.isArray(selectedValue)
      ? selectedValue.filter((opt) => opt?.id !== optionIdToRemove)
      : [];
    setSelectedValue(nextSelectedValue);
    onSelect(nextSelectedValue);
  };

  const clearSelectionHandler = () => {
    if (isDisabled) return;
    if (isMulti) {
      setSelectedValue([]);
      onSelect([]);
    } else {
      setSelectedValue("");
      onSelect(undefined);
    }
  };

  // handlers
  const selectClickedHandler = () => {
    if (isDisabled) {
      return;
    }
    setOpen((prevOpen) => !prevOpen);
  };
  const searchTermChangedHandler = (e) => {
    setSearchTerm(e.target.value);
  };

  // helper
  const getLabelToDisplay = useCallback(
    (option) => {
      if (optionLabelProp === "year") {
        return `${option.startYear}-${option.endYear}`;
      } else if (optionLabelProp === "transmission") {
        return `${option.transmission}-${option.cc}`;
      } else {
        return option[optionLabelProp];
      }
    },
    [optionLabelProp]
  );

  // initial filtered options
  useEffect(() => {
    setFilteredOptions(options);
    const resolvedInitialValue =
      typeof initialValue === "function" ? initialValue() : initialValue;
    if (isMulti) {
      setSelectedValue(
        Array.isArray(resolvedInitialValue) ? resolvedInitialValue : []
      );
    } else {
      setSelectedValue(resolvedInitialValue || "");
    }
  }, [options, initialValue, isMulti]);

  // filter results
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(
          (option) =>
            getLabelToDisplay(option)
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) !== -1
        )
      );
    }
  }, [searchTerm, getLabelToDisplay, options]);

  // handle option click
  const optionClickedHandler = (option) => {
    if (isMulti) {
      const nextSelectedValue = Array.isArray(selectedValue)
        ? selectedValue.some((opt) => opt?.id === option?.id)
          ? selectedValue.filter((opt) => opt?.id !== option?.id)
          : [...selectedValue, option]
        : [option];

      setSelectedValue(nextSelectedValue);
      onSelect(nextSelectedValue);
      return;
    }

    setOpen(false);
    onSelect(option);
  };

  // click away handler
  useEffect(() => {
    const mousedownHandler = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", mousedownHandler);

    return () => {
      document.removeEventListener("mousedown", mousedownHandler);
    };
  }, [selectRef]);

  // the view
  return (
    <div className="relative" ref={selectRef} dir={contentDirection}>
      <div
        className={classNames(
          "border rounded p-2 flex justify-between items-center",
          {
            "cursor-not-allowed bg-gray-200": isDisabled,
          },
          { "cursor-pointer": !isDisabled }
        )}
        role="button"
        onClick={selectClickedHandler}
      >
        {isMulti ? (
          selectedValue?.length ? (
            <div className="flex flex-wrap gap-1 items-center">
              {selectedValue.map((opt) => (
                <span
                  key={opt?.id ?? getLabelToDisplay(opt)}
                  className="bg-gray-100 border border-gray-300 rounded px-2 py-0.5 text-sm flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="truncate max-w-40">
                    {getLabelToDisplay(opt)}
                  </span>
                  <button
                    type="button"
                    className="text-gray-600 hover:text-gray-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedValueHandler(opt?.id);
                    }}
                    aria-label="Remove"
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )
        ) : selectedValue ? (
          <span>{selectedValue}</span>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {allowClear &&
            !isDisabled &&
            (isMulti
              ? Boolean(selectedValue?.length)
              : Boolean(selectedValue)) && (
              <button
                type="button"
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelectionHandler();
                }}
                aria-label="Clear selection"
              >
                x
              </button>
            )}
          <AiOutlineArrowDown
            className={classNames("transition-all", {
              "rotate-0": !open,
              "rotate-180": open,
            })}
          />
        </div>
      </div>
      {open && (
        <div className="absolute right-0 w-full rounded z-30 shadow-lg">
          {isLoading ? (
            <div className="p-4 text-center text-dark-blue flex items-center justify-center">
              <ImSpinner2 className="animate-spin mr-2" />
            </div>
          ) : (
            <>
              <div className="p-2 border rounded bg-white">
                <input
                  type="text"
                  dir="rtl"
                  placeholder="ابحث بالإسم"
                  value={searchTerm}
                  onChange={searchTermChangedHandler}
                  className="block w-full text-right border rounded focus:border-dark-blue focus:outline focus:outline-offset-2 focus:outline-1 p-1 rounded"
                />
              </div>
              <ul
                className="bg-white max-h-52 overflow-y-auto scrollbar-hide text-right"
                dir={optionsDirection}
              >
                {filteredOptions.length === 0 ? (
                  <p className="p-4 text-center text-dark-blue">No Data</p>
                ) : (
                  filteredOptions.map((option, index) => (
                    <li
                      key={index}
                      className={classNames(
                        "p-2 cursor-pointer hover:bg-dark-blue hover:text-white",
                        {
                          "bg-dark-blue text-white": isMulti
                            ? selectedValue?.some(
                                (opt) => opt?.id === option?.id
                              )
                            : selectedValue === getLabelToDisplay(option),
                        }
                      )}
                      onClick={() => optionClickedHandler(option)}
                    >
                      {getLabelToDisplay(option)}
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
